import type { HTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

// Wave 334F · expanded tone vocabulary.
//
// Pages historically used a couple of vocabularies for the same colors:
//   - 'ok' / 'warn' / 'err'  (Builder-native, semantic)
//   - 'success' / 'warning' / 'danger'  (web-standard, SemanticUI-ish)
//
// Until now Badge only accepted the first set, so 6 pages threw tsc
// errors. We accept both via aliasing — same visual output, less
// fighting the typecker. Going forward, prefer the Builder set.
type CanonicalTone = 'neutral' | 'brand' | 'ok' | 'warn' | 'err' | 'muted';
type AliasTone = 'success' | 'warning' | 'danger';
type Tone = CanonicalTone | AliasTone;
type Size = 'sm' | 'md';

function canonical(tone: Tone): CanonicalTone {
  switch (tone) {
    case 'success': return 'ok';
    case 'warning': return 'warn';
    case 'danger': return 'err';
    default: return tone;
  }
}

/**
 * Badges read as small pill-shaped status labels. Backgrounds are tinted
 * so they pop in light mode without being loud; in dark mode the same
 * opacity yields the right muted glow against surface-2.
 *
 * The `solid` prop swaps to a filled chip — used on the timeline rail
 * for event type pills where the type *is* the identity, not just a hint.
 */
const outline: Record<CanonicalTone, string> = {
  neutral:
    'bg-surface-3 text-ink border border-line',
  brand:
    'bg-brand-400/12 text-brand-600 border border-brand-400/25 dark:text-brand-500',
  ok:
    'bg-ok/12 text-ok border border-ok/25',
  warn:
    'bg-warn/12 text-warn border border-warn/25',
  err:
    'bg-err/12 text-err border border-err/25',
  muted:
    'bg-surface-3/60 text-ink-muted border border-line',
};

const solid: Record<CanonicalTone, string> = {
  neutral: 'bg-ink text-surface',
  brand: 'bg-brand-500 text-white',
  ok: 'bg-ok text-white',
  warn: 'bg-warn text-white',
  err: 'bg-err text-white',
  muted: 'bg-surface-4 text-ink',
};

const sizes: Record<Size, string> = {
  sm: 'h-[22px] px-1.5 text-[11px] font-medium tracking-wide',
  md: 'h-6 px-2 text-xs font-medium',
};

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: Tone;
  size?: Size;
  solid?: boolean;
}

export function Badge({
  tone = 'neutral',
  size = 'md',
  solid: isSolid,
  className,
  ...rest
}: BadgeProps) {
  const t = canonical(tone);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full whitespace-nowrap',
        isSolid ? solid[t] : outline[t],
        sizes[size],
        className,
      )}
      {...rest}
    />
  );
}
